import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import {
  ChartColumn,
  Users,
  BadgeIndianRupee,
  Wallet,
  TrendingUp,
  Trophy,
  ArrowLeft,
  Calendar,
} from "lucide-react";
import moment from "moment";
import DatePicker from "react-datepicker";
import Skeleton from "react-loading-skeleton";
import "react-datepicker/dist/react-datepicker.css";
import axiosInstance from "../../../services/api/axios";
import AgentPerformance from "./AgentPerformance";
import SalesSummary from "./SalesSummary";

Chart.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

/* ---------- formatters ---------- */
const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)} K`;
  return `₹${Math.round(v)}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtPct = (n) => (Math.trunc(Number(n || 0) * 100) / 100).toFixed(2);

const PERIODS = [
  { value: "custom", label: "Custom range" },
  { value: "monthly", label: "Last month" },
  { value: "quarterly", label: "Last quarter" },
  { value: "yearly", label: "Last year" },
  { value: "all", label: "All time" },
];

const TABS = [
  {
    label: "Sales team",
    value: "sales",
    icon: BadgeIndianRupee,
    description: "Per-login booking totals",
  },
  {
    label: "Agents",
    value: "agent",
    icon: Users,
    description: "Channel partner contribution",
  },
];

const KpiTile = ({ icon: Icon, label, value, subtitle, accent = "slate" }) => {
  const accents = {
    flamingo: "bg-flamingo-50 text-flamingo-600",
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-600",
    indigo: "bg-indigo-50 text-indigo-600",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accents[accent]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums truncate">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1 truncate">{subtitle}</p>}
      </div>
    </div>
  );
};

const TileSkeleton = () => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 h-32">
    <Skeleton circle height={40} width={40} />
    <Skeleton height={12} width="70%" className="mt-3" />
    <Skeleton height={28} width="50%" className="mt-2" />
  </div>
);

function PerformanceDashBoard() {
  const navigate = useNavigate();

  /* ---------- tab from hash ---------- */
  const [activeTab, setActiveTab] = useState("sales");
  useEffect(() => {
    if (!window.location.hash) {
      navigate("/reports/performance#sales", { replace: true });
      setActiveTab("sales");
    } else {
      setActiveTab(window.location.hash.slice(1) || "sales");
    }
    const handleHashChange = () =>
      setActiveTab(window.location.hash.slice(1) || "sales");
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [navigate]);
  const handleTabChange = (value) => {
    setActiveTab(value);
    navigate(`/reports/performance#${value}`, { replace: true });
  };

  /* ---------- filters ---------- */
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [projectOptions, setProjectOptions] = useState([]);
  const [periodFilter, setPeriodFilter] = useState("quarterly");
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "quarters").startOf("quarter").toDate()
  );
  const [endDate, setEndDate] = useState(
    moment().subtract(1, "quarters").endOf("quarter").toDate()
  );

  useEffect(() => {
    if (periodFilter === "custom" || periodFilter === "all") return;
    let s;
    let e;
    if (periodFilter === "monthly") {
      s = moment().subtract(1, "months").startOf("month").toDate();
      e = moment().subtract(1, "months").endOf("month").toDate();
    } else if (periodFilter === "quarterly") {
      s = moment().subtract(1, "quarters").startOf("quarter").toDate();
      e = moment().subtract(1, "quarters").endOf("quarter").toDate();
    } else if (periodFilter === "yearly") {
      s = moment().subtract(1, "years").startOf("year").toDate();
      e = moment().subtract(1, "years").endOf("year").toDate();
    }
    setStartDate(s);
    setEndDate(e);
  }, [periodFilter]);

  /* ---------- project picker ---------- */
  useEffect(() => {
    let mounted = true;
    axiosInstance
      .get("/dashboard/projects")
      .then((res) => {
        if (mounted) setProjectOptions(Array.isArray(res) ? res : res?.items || []);
      })
      .catch(() => {
        if (mounted) setProjectOptions([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  /* ---------- summary ---------- */
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    const params = { isAgent: activeTab === "agent" };
    if (periodFilter !== "all") {
      params.startDate = moment(startDate).format("YYYY-MM-DD");
      params.endDate = moment(endDate).format("YYYY-MM-DD");
    }
    if (selectedProjectId) params.projectId = selectedProjectId;
    axiosInstance
      .get("/reports/performance-summary", { params })
      .then((res) => {
        if (mounted) setSummary(res);
      })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.detail || err.message || "Failed to load");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [activeTab, selectedProjectId, periodFilter, startDate, endDate]);

  /* ---------- derived ---------- */
  const entities = summary?.entities || [];
  const topPerformer = entities[0];
  const avgPerEntity = useMemo(() => {
    if (entities.length === 0) return 0;
    return (summary?.totalRevenue || 0) / entities.length;
  }, [entities.length, summary]);

  const barChart = useMemo(() => {
    const top10 = entities.slice(0, 10);
    return {
      data: {
        labels: top10.map((e) => e.name),
        datasets: [
          {
            label: "Revenue",
            data: top10.map((e) => Number(e.revenue || 0)),
            backgroundColor: top10.map((_, i) =>
              i === 0
                ? "#f97316"
                : i === 1
                ? "#fb923c"
                : i === 2
                ? "#fdba74"
                : "#475569"
            ),
            borderRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: "y",
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => fmtINR(ctx.parsed.x) } },
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: { callback: (v) => fmtINR(v), font: { size: 11 } },
            grid: { color: "#f1f5f9" },
          },
          y: { grid: { display: false }, ticks: { font: { size: 11 } } },
        },
      },
    };
  }, [entities]);

  const selectedProjectLabel =
    selectedProjectId === ""
      ? "All projects"
      : projectOptions.find((p) => String(p.projectId) === String(selectedProjectId))
          ?.projectName || "Project";

  const dateRangeLabel =
    periodFilter === "all"
      ? "All time"
      : `${moment(startDate).format("DD MMM YYYY")} – ${moment(endDate).format("DD MMM YYYY")}`;

  const active = TABS.find((t) => t.value === activeTab) || TABS[0];

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)] bg-slate-50/40">
      {/* Hero */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              <ChartColumn size={12} /> Reports
            </p>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">
              Performance dashboard
            </h1>
            <p className="text-xs text-slate-300 mt-1 flex items-center gap-1.5">
              <Calendar size={12} />
              {dateRangeLabel}
              <span className="mx-1.5 text-slate-500">•</span>
              {selectedProjectLabel}
              <span className="mx-1.5 text-slate-500">•</span>
              {active.description}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-200 hover:text-white self-start lg:self-end"
          >
            <ArrowLeft size={14} /> Back
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">Project</label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none"
            >
              <option value="" className="text-slate-900">
                All projects
              </option>
              {projectOptions.map((p) => (
                <option key={p.projectId} value={p.projectId} className="text-slate-900">
                  {p.projectName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">Period</label>
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none"
            >
              {PERIODS.map((p) => (
                <option key={p.value} value={p.value} className="text-slate-900">
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">From</label>
            <div className="relative">
              <DatePicker
                selected={startDate}
                onChange={(d) => {
                  setStartDate(d);
                  setPeriodFilter("custom");
                }}
                dateFormat="yyyy-MM-dd"
                disabled={periodFilter === "all"}
                className="px-3 py-2 pl-8 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none w-full disabled:opacity-50"
              />
              <Calendar
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-wider text-slate-300">To</label>
            <div className="relative">
              <DatePicker
                selected={endDate}
                onChange={(d) => {
                  setEndDate(d);
                  setPeriodFilter("custom");
                }}
                dateFormat="yyyy-MM-dd"
                disabled={periodFilter === "all"}
                className="px-3 py-2 pl-8 rounded-lg bg-white/10 text-white text-sm border border-white/20 focus:bg-white/20 focus:border-white/40 focus:outline-none w-full disabled:opacity-50"
              />
              <Calendar
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tab nav */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 inline-flex gap-1 self-start flex-wrap">
        {TABS.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => handleTabChange(value)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === value
                ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
          </>
        ) : (
          <>
            <KpiTile
              icon={BadgeIndianRupee}
              label="Total bookings"
              value={fmtInt(summary?.totalSales)}
              subtitle={`across ${fmtInt(entities.length)} ${
                activeTab === "agent" ? "agents" : "team members"
              }`}
              accent="flamingo"
            />
            <KpiTile
              icon={Wallet}
              label="Total revenue"
              value={fmtINR(summary?.totalRevenue)}
              subtitle={`Avg ${fmtINR(avgPerEntity)} / ${
                activeTab === "agent" ? "agent" : "member"
              }`}
              accent="emerald"
            />
            <KpiTile
              icon={TrendingUp}
              label={activeTab === "agent" ? "Total commission" : "Conversion ratio"}
              value={
                activeTab === "agent"
                  ? fmtINR(summary?.totalCommission)
                  : `${fmtPct(
                      summary?.totalRevenue && summary.totalSales
                        ? (summary.totalSales / Math.max(1, entities.length)) * 1
                        : 0
                    )}`
              }
              subtitle={
                activeTab === "agent"
                  ? "Paid out across all agents"
                  : "Avg bookings per member"
              }
              accent="indigo"
            />
            <KpiTile
              icon={Trophy}
              label="Top performer"
              value={topPerformer?.name || "—"}
              subtitle={
                topPerformer
                  ? `${fmtInt(topPerformer.salesCount)} sales · ${fmtINR(topPerformer.revenue)}`
                  : "No data"
              }
              accent="amber"
            />
          </>
        )}
      </div>

      {/* Leaderboard chart */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="p-5 pb-2 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Revenue leaderboard</h3>
            <p className="text-xs text-slate-500">Top 10 ranked by revenue</p>
          </div>
        </div>
        <div className="p-5 pt-2 h-80">
          {loading ? (
            <Skeleton height={280} />
          ) : entities.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-slate-500">
              No bookings in this range
            </div>
          ) : (
            <Bar data={barChart.data} options={barChart.options} />
          )}
        </div>
      </div>

      {/* Ranked table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Full ranking</h3>
          <p className="text-xs text-slate-500">Every {activeTab === "agent" ? "agent" : "team member"} with at least one booking</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                <th className="text-left px-5 py-3 font-semibold">#</th>
                <th className="text-left px-5 py-3 font-semibold">Name</th>
                <th className="text-left px-5 py-3 font-semibold">Bookings</th>
                <th className="text-left px-5 py-3 font-semibold">Revenue</th>
                <th className="text-left px-5 py-3 font-semibold">Share</th>
                {activeTab === "agent" && (
                  <th className="text-left px-5 py-3 font-semibold">Commission</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={activeTab === "agent" ? 6 : 5} className="px-5 py-3">
                      <Skeleton height={24} />
                    </td>
                  </tr>
                ))
              ) : entities.length === 0 ? (
                <tr>
                  <td
                    colSpan={activeTab === "agent" ? 6 : 5}
                    className="px-5 py-8 text-center text-sm text-slate-500"
                  >
                    No bookings in this range
                  </td>
                </tr>
              ) : (
                entities.map((e, i) => {
                  const share =
                    summary.totalRevenue > 0 ? (e.revenue / summary.totalRevenue) * 100 : 0;
                  return (
                    <tr key={e.entityId} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3 text-sm font-semibold text-slate-500 tabular-nums">
                        {i + 1}
                      </td>
                      <td className="px-5 py-3 text-sm font-medium text-slate-900">
                        {e.name}
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-700 tabular-nums">
                        {fmtInt(e.salesCount)}
                      </td>
                      <td className="px-5 py-3 text-sm font-semibold text-slate-900 tabular-nums">
                        {fmtINR(e.revenue)}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-flamingo-400 to-flamingo-600"
                              style={{ width: `${Math.min(100, share)}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-600 tabular-nums">
                            {fmtPct(share)}%
                          </span>
                        </div>
                      </td>
                      {activeTab === "agent" && (
                        <td className="px-5 py-3 text-sm text-slate-700 tabular-nums">
                          {fmtINR(e.commission)}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Existing detail child — kept available below the new dashboard for
          drill-down into per-project breakdowns. */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Per-project breakdown</h3>
          <p className="text-xs text-slate-500">
            Detailed view across projects ({activeTab === "agent" ? "agents" : "sales team"})
          </p>
        </div>
        <div className="p-4 sm:p-5">
          {activeTab === "sales" ? <SalesSummary /> : <AgentPerformance />}
        </div>
      </div>
    </div>
  );
}

export default PerformanceDashBoard;
